import React from 'react';
import { Box, Typography, Button, Container, Stack } from '@mui/material';
import { Icon } from '@iconify/react';

const HeroSection: React.FC = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      window.location.hash = sectionId;
    }
  };

  return (
    <Box
      id='hero'
      sx={{
        background: (theme) =>
          `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
        color: '#fff',
        py: { xs: 8, md: 12 },
        position: 'relative',
      }}
    >
      {/* Background gradient circles */}
      <Box
        sx={{
          position: 'absolute',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(127, 0, 255, 0.3) 0%, rgba(127, 0, 255, 0) 70%)',
          top: '0px',
          right: '-100px',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(127, 0, 255, 0.2) 0%, rgba(127, 0, 255, 0) 70%)',
          bottom: '-200px',
          left: '-200px',
          zIndex: 0,
        }}
      />

      <Container maxWidth='lg' sx={{ position: 'relative', zIndex: 1 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={4}
          alignItems='center'
        >
          {/* Text Content */}
          <Box sx={{ width: { xs: '100%', md: '50%' } }}>
            <Typography
              variant='h2'
              component='h1'
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                background: (theme) =>
                  `linear-gradient(90deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.main} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Skyfall Enterprises.
            </Typography>
            <Typography
              variant='h5'
              component='h2'
              sx={{ mb: 1, fontWeight: 500 }}
            >
              Your guide in the{' '}
              <Box component='span' sx={{ color: 'primary.light' }}>
                digital age.
              </Box>
            </Typography>
            <Typography
              variant='body1'
              sx={{ mb: 4, opacity: 0.8, maxWidth: '90%' }}
            >
              We provide cutting-edge solutions to help your business thrive in
              today's competitive digital landscape.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant='contained'
                size='large'
                onClick={() => scrollToSection('services')}
                sx={{
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: '#6a00d6' },
                  borderRadius: '30px',
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Get Started
              </Button>
              <Button
                variant='outlined'
                size='large'
                onClick={() => scrollToSection('about')}
                sx={{
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': {
                    borderColor: '#fff',
                    bgcolor: 'rgba(255,255,255,0.05)',
                  },
                  borderRadius: '30px',
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Learn More
              </Button>
            </Box>
          </Box>

          {/* Visual Content */}
          <Box
            sx={{
              width: { xs: '100%', md: '50%' },
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: { xs: '300px', md: '400px' },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {/* Feature Icons */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '10%',
                  left: '15%',
                  animation: 'pulse 2s infinite',
                }}
              >
                <Icon
                  icon='mdi:web'
                  width='50'
                  height='50'
                  style={{ color: 'primary.main' }}
                />
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: '40%',
                  right: '15%',
                  animation: 'pulse 2.5s infinite',
                }}
              >
                <Icon
                  icon='mdi:database'
                  width='50'
                  height='50'
                  style={{ color: 'primary.main' }}
                />
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '15%',
                  left: '25%',
                  animation: 'pulse 3s infinite',
                }}
              >
                <Icon
                  icon='mdi:cellphone'
                  width='50'
                  height='50'
                  style={{ color: 'primary.main' }}
                />
              </Box>

              {/* Central Circle */}
              <Box
                sx={{
                  width: '200px',
                  height: '200px',
                  borderRadius: '50%',
                  background: 'rgba(127, 0, 255, 0.1)',
                  border: '1px solid rgba(127, 0, 255, 0.3)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: '0 0 30px rgba(127, 0, 255, 0.3)',
                }}
              >
                <Icon
                  icon='mdi:rocket-launch'
                  width='80'
                  height='80'
                  style={{ color: 'primary.main' }}
                />
              </Box>
            </Box>
          </Box>
        </Stack>
      </Container>
      {/* Add keyframes for pulse animation */}
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 0.7; }
            50% { transform: scale(1.1); opacity: 1; }
            100% { transform: scale(1); opacity: 0.7; }
          }
        `}
      </style>
    </Box>
  );
};

export default HeroSection;
